import React, { useEffect, useState } from "react";
import CardImage from "../../../../assets/image/icons/CreditCard.png";
import AxiosInstance from "../../../AxiosInstance";
import sendToast from "../../../../components/Toast/sendToast";
import AddAchAccount from "../../Account/AddAchAccount";
import "./style.css";
import { Grid, Typography } from "@mui/material";
import { LoaderComponent } from "../../../../components/Icon/Index";
import BlueButton from "../../../../components/Button/BlueButton";
import { useUserType } from "../../../../contexts/UserTypeContext";
import Delete from "../../../../assets/image/icons/delete.svg";
import sendSwal from "../../../../components/Swal/sendSwal";
import showToast from "../../../../components/Toast/Toster";
import { getTokenizationKey } from "../../../../plugins/ApiHandler";
import { getItem } from "../../../../utils/storage";

const AchPayment = ({
  paymentFormik,
  selectedAccount,
  setSelectedAccount,
  companyData,
  CustomerId,
  tokenDecode,
  onBeforeDeleteConfirm,
}) => {
    
  const [accountDetails, setAccountDetails] = useState([]);
  const [loader, setloader] = useState(true);
  const [isPublicKeyAvailable, setIsPublicKeyAvailable] = useState(false);
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  
  const fetchAccountData = async () => {
    if (!CustomerId) {
      sendToast("We couldn't load saved ACH accounts. Please select a customer and try again.");
      return;
    }
    const CompanyId = getItem("CompanyId") || tokenDecode?.CompanyId || companyData?.CompanyId;
    // Use ACH gateway (e.g. NMI) so existing ACH accounts are loaded even when Dejavoo (card) was added first
    const gatewayForAch = tokenDecode?.GatewayAch ?? tokenDecode?.data?.GatewayAch ?? companyData?.GatewayAch ?? tokenDecode?.Gateway ?? companyData?.Gateway;
    try {
      const res = await AxiosInstance.post("/v1/nmi/get-billing-customer-vault", {
        CompanyId,
        type: "ach",
        CustomerId,
        Gateway: gatewayForAch,
      });
      const billing = res?.data?.data?.customer?.billing;
      if (
        res?.data?.status === 200 &&
        res?.data?.data &&
        res?.data?.data?.customer &&
        res?.data?.data?.customer?.billing
      ) {
        paymentFormik?.setFieldValue(
          "customer_vault_id",
          res?.data?.data?.customer["@attributes"]?.id
        );
        if (Array.isArray(res?.data?.data?.customer?.billing)) {
          setAccountDetails(res?.data?.data?.customer?.billing);
        } else {
          setAccountDetails([res?.data?.data?.customer?.billing]);
        }
      } else {
        setAccountDetails([]);
      }
    } catch (error) {
      sendToast("We couldn't load saved ACH accounts. Please try again in a moment.");
    } finally {
      setloader(false);
    }
  };

  const getNmiKey = async (Company_Id) => {
    try {
      const keyResponse = await getTokenizationKey(Company_Id);
      if (keyResponse?.PublicKey) {
        setIsPublicKeyAvailable(true);
      } else {
        setIsPublicKeyAvailable(false);
      }
    } catch (error) {
      setIsPublicKeyAvailable(false);
    }
  };

  useEffect(() => {
    const CompanyId = getItem("CompanyId") || tokenDecode?.CompanyId || companyData?.CompanyId;
    if (CompanyId) {
      getNmiKey(CompanyId);
    }
  }, [companyData, tokenDecode]);

  useEffect(() => {
    if (CustomerId) {
      setloader(true);
      fetchAccountData();
    } else {
      setAccountDetails([]);
      setloader(false);
    }
  }, [CustomerId]);

  // Function to auto-fill address from selected ACH account
  const autoFillAddressFromAccount = (account) => {
    if (!account) return;
    
    // Extract address fields from the ACH account billing data
    const address1 = account?.address1 || account?.Address || "";
    const city = account?.city || account?.City || "";
    const state = account?.state || account?.State || "";
    const zip = account?.zip || account?.Zip || "";
    const country = account?.country || account?.Country || "";
    
    // Set address fields in paymentFormik if they exist
    if (address1) {
      paymentFormik.setFieldValue("address1", address1);
    }
    if (city) {
      paymentFormik.setFieldValue("city", city);
    }
    if (state) {
      paymentFormik.setFieldValue("state", state);
    }
    if (zip) {
      paymentFormik.setFieldValue("zip", zip);
    }
    if (country) {
      paymentFormik.setFieldValue("country", country);
    }
  };

  useEffect(() => {
    if (accountDetails?.length === 1 && !selectedAccount) {
      const singleAccount = accountDetails[0];
      setSelectedAccount(singleAccount);
      paymentFormik.setFieldValue("billing_id", singleAccount["@attributes"]?.id);
      // Auto-fill address when account is auto-selected
      autoFillAddressFromAccount(singleAccount);
    }
  }, [accountDetails, selectedAccount]);

  // Auto-fill address when account is manually selected
  useEffect(() => {
    if (selectedAccount) {
      autoFillAddressFromAccount(selectedAccount);
    }
  }, [selectedAccount]);

  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = async (item) => {
    onBeforeDeleteConfirm?.();
    sendSwal(isDonationMode).then(async (willDelete) => {
      if (willDelete) {
        try {
          if (item["@attributes"]?.id) {
            const storedCompanyId = getItem("CompanyId");
            const finalCompanyId = tokenDecode?.CompanyId || storedCompanyId || companyData?.CompanyId;

            const object = {
              CompanyId: finalCompanyId,
              customer_vault_id: paymentFormik?.values?.customer_vault_id,
              billing_id: item["@attributes"]?.id,
            };
            
            let url;
            if (accountDetails?.length > 1) {
              url = `/v1/nmi/delete-customer-billing`;
            } else {
              url = `/v1/nmi/delete-customer-vault`;
            }

            const deleteRes = await AxiosInstance.post(url, object);
            if (deleteRes?.data?.status === 200) {
              showToast.success("ACH account deleted");
              fetchAccountData();
            } else {
              const errMsg =
                deleteRes?.data?.data?.error ||
                deleteRes?.data?.error ||
                "Failed to delete ACH account.";
              showToast.error(errMsg);
            }
          }
        } catch (error) {
          const errMsg =
            error?.response?.data?.data?.error ||
            error?.response?.data?.error ||
            error?.message ||
            "We couldn't delete this ACH account. Please try again in a moment.";
          showToast.error(errMsg);
        }
      }
    });
  };

  const handleAccountAdded = () => {};

  const formatAchAccountMasked = (item) => {
    // Use stored masked account from backend (e.g. "12****7890") – show as stored
    const account = String(
      item?.displayAccountNumber ||
      item?.checkaccount ||
      item?.["@attributes"]?.checkaccount ||
      item?.check_account ||
      item?.["@attributes"]?.check_account ||
      ""
    ).trim();
    if (!account) return "••••••••";
    if (account.length >= 4) {
      const isAlreadyMasked = /[*x]/i.test(account);
      return isAlreadyMasked ? account : `****${account.replace(/\D/g, "").slice(-4)}`;
    }
    return account;
  };

  const formatAchTypeHolder = (item) => {
    const rawAccountType = item?.account_type ?? item?.["@attributes"]?.account_type ?? "";
    const rawHolderType = item?.account_holder_type ?? item?.["@attributes"]?.account_holder_type ?? "";
    // Ensure we never render [object Object] - extract string from primitives or objects
    const accountType =
      typeof rawAccountType === "string"
        ? rawAccountType.trim()
        : typeof rawAccountType === "object" && rawAccountType !== null
        ? String(rawAccountType?.value ?? rawAccountType?.name ?? "").trim()
        : "";
    const holderType =
      typeof rawHolderType === "string"
        ? rawHolderType.trim()
        : typeof rawHolderType === "object" && rawHolderType !== null
        ? String(rawHolderType?.value ?? rawHolderType?.name ?? "").trim()
        : "";
    const parts = [];
    if (accountType) parts.push(accountType.charAt(0).toUpperCase() + accountType.slice(1));
    if (holderType) parts.push(holderType.charAt(0).toUpperCase() + holderType.slice(1));
    return parts.length ? `ACH - ${parts.join(" ")}` : "ACH";
  };

  return (
    <Grid
      className="mb-3 px-2 d-flex flex-column accountInformationInputTop creditCardBoxSelectDate customer-ach-payment-list"
      sx={{
        background: "transparent",
        borderRadius: "8px",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "visible",
        overflowY: "visible",
        boxSizing: "border-box",
      }}
    >
      {loader ? (
        <Grid className="d-flex flex-direction-row justify-content-center align-items-center p-5 m-5">
          <LoaderComponent variant="skeleton"
            loader={loader}
            isDonationMode={isDonationMode}
            height="50"
            width="50"
          />
        </Grid>
      ) : accountDetails && accountDetails?.length > 0 ? (
        <div className="payment-cards-container">
          {accountDetails?.map((item, index) => {
            const isSelected = selectedAccount === item;
            return (
              <div
                key={index}
                className={`payment-card-row ${isSelected ? "selected" : ""}`}
                onClick={(e) => {
                  if (!e.target.closest(".delete-card-btn")) {
                    setSelectedAccount(item);
                    paymentFormik.setFieldValue("billing_id", item["@attributes"]?.id);
                    autoFillAddressFromAccount(item);
                  }
                }}
              >
                <div className="card-radio-container">
                  <input
                    type="radio"
                    className="payment-card-radio"
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAccount(item);
                        paymentFormik.setFieldValue("billing_id", item["@attributes"]?.id);
                        autoFillAddressFromAccount(item);
                      } else {
                        setSelectedAccount(null);
                        paymentFormik.setFieldValue("billing_id", "");
                        paymentFormik.setFieldValue("address1", "");
                        paymentFormik.setFieldValue("city", "");
                        paymentFormik.setFieldValue("state", "");
                        paymentFormik.setFieldValue("zip", "");
                        paymentFormik.setFieldValue("country", "");
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAccount(item);
                      paymentFormik.setFieldValue("billing_id", item["@attributes"]?.id);
                      autoFillAddressFromAccount(item);
                    }}
                  />
                </div>
                <div className="card-logo-container">
                  <img src={CardImage} alt="Bank" width="90%" height="90%" style={{ objectFit: "contain" }} />
                </div>
                <div className="card-details-container">
                  <div className="card-number-text">
                    {formatAchAccountMasked(item)}
                  </div>
                  <div className="card-expiry-text">
                    {formatAchTypeHolder(item)}
                  </div>
                </div>
                <div className="card-delete-container">
                  <div
                    className="delete-card-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item);
                    }}
                  >
                    <img src={Delete} alt="Delete" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : !isPublicKeyAvailable ? (
        <Grid className="d-flex align-items-center justify-content-start w-100 my-3" style={{ overflowX: "auto" }}>
          <Typography style={{ color: "#666666", fontSize: "14px", fontStyle: "italic" }}>
            No payment gateway configured
          </Typography>
        </Grid>
      ) : (
        <>
          <Grid
            className="d-flex align-items-center justify-content-start w-100 my-3"
            style={{ overflowX: "auto" }}
          >
            <Grid className="d-flex align-items-center w-100">
              <Grid
                style={{
                  width: "44px",
                  height: "44px",
                  background: "#F0F0F0",
                  borderRadius: "50%",
                  padding: "8px 5px",
                }}
                className="d-flex justify-content-center align-items-center"
              >
                <img src={CardImage} alt="" width="90%" height="90%" />
              </Grid>
              <Grid className="d-flex flex-column justify-conent-center mx-2">
                <Typography className="text-blue-color">No ACH Account</Typography>
              </Grid>
            </Grid>
            <Grid className="d-flex justify-content-end mx-2">
              {isPublicKeyAvailable && (
                <BlueButton
                  className="p-2 bg-blue-color text-white-color"
                  style={{ borderRadius: "4px", fontSize: "12px" }}
                  isDonationMode={isDonationMode}
                  onClick={() => setIsOpen(true)}
                  label={
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 3V13M3 8H13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <span>Add Account</span>
                    </>
                  }
                />
              )}
            </Grid>
          </Grid>
        </>
      )}

      <AddAchAccount
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        companyData={companyData}
        isBilling={accountDetails?.length > 0}
        vaultId={paymentFormik?.values?.customer_vault_id}
        fetchData={fetchAccountData}
        CustomerId={CustomerId}
        onAccountAdded={handleAccountAdded}
        Gateway={((tokenDecode?.Gateway || tokenDecode?.data?.Gateway || companyData?.Gateway) || "").toString().toUpperCase() === "DEJAVOO" ? "DEJAVOO" : (tokenDecode?.Gateway || tokenDecode?.data?.Gateway || companyData?.Gateway)}
      />

      {accountDetails && accountDetails?.length > 0 && isPublicKeyAvailable && (
        <Grid className="d-flex justify-content-end mb-3 mx-2 AddCardButtonBoxPayment">
          <BlueButton
            className="p-2 text-white-color"
            isDonationMode={isDonationMode}
            style={{ borderRadius: "4px", fontSize: "12px" }}
            onClick={() => setIsOpen(true)}
            label={
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3V13M3 8H13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>Add Account</span>
              </>
            }
          />
        </Grid>
      )}
    </Grid>
  );
};

export default AchPayment;
